import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Message, PrivilegeType, TableData } from 'src/app/shared/models/domain';
import { CommonService } from 'src/app/shared/services/common.service';
import { DataService } from 'src/app/shared/services/data.service';
import { ConfirmationPopupComponent } from 'src/app/shared/component/confirmation-popup/confirmation-popup.component';
import { Milestone, Project } from 'src/app/core/models/project-pilot.model';
import { ProjectPilotService } from 'src/app/shared/services/project-pilot.service';
import { ManageMilestoneComponent } from '../manage-milestone/manage-milestone.component';
import { DomainService } from 'src/app/shared/services/domain.service';
import { formatDate } from '@angular/common';

@Component({
  selector: 'app-milestones',
  templateUrl: './milestones.component.html',
  styleUrls: ['./milestones.component.scss']
})
export class MilestonesComponent implements OnInit {

  public milestones: Milestone[] = [];
  public projects: any[] = [];
  public statuses: string[] = [];
  public priorities: string[];
  public projectId: number | null = null;
  public selectedStatus: string = '';
  public selectedPriority: string = '';
  public isLoading: boolean = true;
  public searchKeyword: string = "";
  public privilege: PrivilegeType;
  public tableData: TableData = { content: [], number: 1, size: 10, totalElements: 0 } as TableData;
  public pageSizes: number[] = [];
  private pageDataKey: string = 'm-l';
  public selectedFilter: string = '';
  public filters: string[] = [];
  public fromDate: Date;
  public toDate: Date;
  public filter: any;

  constructor(
    private modalService: NgbModal,
    private commonService: CommonService,
    private dataService: DataService,
    private projectPilotSevice: ProjectPilotService,
    private domainService: DomainService
  ) { }

  ngOnInit(): void {
    this.selectedStatus = null;
    this.selectedPriority = null;
    this.filters = this.domainService.getDateFiltersTypes();
    this.selectedFilter = this.filters[2];
    this.filter = {
      from: "",
      to: "",
    }
    this.pageSizes = this.commonService.getDefaultPageSizes();
    this.tableData = this.commonService.getDefaultPageData(this.pageDataKey);
    this.getStatuses();
    this.priorities = this.commonService.getPriorities();
    this.projectPilotSevice.fetchProjects(false).subscribe((projects: Project[]) => {
      this.projects = projects;
      if (this.projects.length > 0) {
        const selectedProject = this.projects[0];
        this.projectId = selectedProject.id;
        this.getMilestones();
      }
    });
    this.dataService.privilege().subscribe(privilege => { this.privilege = this.commonService.getPrivileges(privilege) });
  }

  public onProjectChange() {
    if (this.projectId) {
      this.getMilestones();
    }
  }

  private getStatuses(): void {
    this.statuses = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'ON_HOLD'];
  }
  private getMilestones(clear: boolean = true, pageNumber: number = 0) {
      if (clear) {
        this.isLoading = true;
        this.tableData.content = [];
      }
      const pageSize = Number(this.tableData.size);
        this.projectPilotSevice.getMilestonesByPage(pageNumber, pageSize, null).subscribe({
        next: (tableData: TableData) => {
          this.tableData = {
            ...tableData,
            number: pageNumber + 1,  
          };
          this.tableData.size = pageSize;
          this.tableData.content = this.tableData?.content?.sort((a, b) => a.id - b.id);
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.commonService.setToastr(500, error?.error?.name || error.error);
        }
      });
  }

  public upsertMilestone(milestone?: Milestone) {
    if (!this.privilege.canEdit) return;
    const modalRef = this.modalService.open(ManageMilestoneComponent, { size: 'xl', backdrop: 'static' });
    modalRef.componentInstance.milestone = milestone;
    modalRef.componentInstance.milestones = this.milestones.filter(m => m.id !== milestone?.id);
    modalRef.componentInstance.passEntry.subscribe((response: Message) => {
      if (!response?.failed) {
        this.getMilestones(false);
      }
    });
  }

  public toggleMilestoneStatus(milestone: Milestone) {
    if (!this.privilege.canEdit) {
      setTimeout(() => { milestone.disabled = !milestone.disabled }, 0);
      return;
    }
    this.projectPilotSevice.toggleMilestoneStatus(milestone).subscribe({
      next: (response: Message) => {
        if (!response.failed) {
          milestone.disabled != milestone.disabled;
          this.commonService.setToastr(200, `Milestone ${milestone.title} is now ${milestone.disabled ? 'disabled' : 'enabled'}!`);
        }
      },
      error: (error: HttpErrorResponse) => {
        milestone.isDeleteInProgress = false;
      },
    });
  }

  public deleteMilestone(milestone: Milestone, index: number) {
    if (!this.privilege.canDelete) return;
    const modalRef = this.modalService.open(ConfirmationPopupComponent);
    modalRef.componentInstance.course = "Milestone deletion";
    modalRef.componentInstance.prefixMessage = "Are you sure you want to delete milestone";
    modalRef.componentInstance.message = milestone?.title;
    modalRef.componentInstance.label = 'Milestone';
    modalRef.result.then((isConfirmed: boolean) => {
      if (isConfirmed) {
        milestone.isDeleteInProgress = true;
        this.projectPilotSevice.deleteMilestone(milestone.id).subscribe({
          next: (message: Message) => {
            milestone.isDeleteInProgress = false;
            if (!message.failed) {
              this.commonService.setToastr(200, message.message);
              this.tableData?.content.splice(index, 1);
            } else {
              const errorMsg = message.text || message.message || "Milestone deletion failed.";
              this.commonService.setToastr(500, errorMsg);
            }
          },
          error: (error: HttpErrorResponse) => {
            milestone.isDeleteInProgress = false;
          },
        });
      }
    });
  }

  public getTruncatedName(name: string, maxLength: number): string {
    if (name.length > maxLength) {
      return name.slice(0, maxLength) + '...';
    }
    return name;
  }

  public loadTableData(e: any) {
    this.tableData.size = +this.tableData.size;
    this.tableData.number = 1;
    this.commonService.storePageData(this.pageDataKey, this.tableData.size, this.tableData.number);
    this.getMilestones(true);
  }

  public onPageChange(newPage: number): void {
    this.tableData.number = newPage; 
    const pageNumber = newPage - 1;  
    this.getMilestones(false, pageNumber);
  }

  public filterAndFetchMilestone(filter: string, projectId: number | null, element?: any) {
    if (!projectId) {
      this.commonService.setToastr(400, 'Please select a project!');
      return;
    }

    const now = new Date();
    const today = new Date(now.setHours(0, 0, 0, 0)); // Set today to 12:00 AM
    let fromDate: Date;
    let toDate: Date;

    switch (filter) {
      case 'Today':
        fromDate = new Date(today);
        toDate = new Date(today);
        toDate.setHours(23, 59, 59, 999); // Set to 11:59:59 PM
        break;
      case 'Yesterday':
        fromDate = new Date(today);
        fromDate.setDate(today.getDate() - 1);
        fromDate.setHours(0, 0, 0, 0);
        toDate = new Date(today);
        toDate.setDate(today.getDate() - 1);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'Last Week':
        fromDate = new Date(today);
        fromDate.setDate(today.getDate() - 7);
        fromDate.setHours(0, 0, 0, 0);
        toDate = new Date(today);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'Custom':
        this.fromDate = this.formatDate(this.fromDate) as any;
        this.toDate = this.formatDate(this.toDate) as any;
        if (!this.fromDate || !this.toDate) {
          this.commonService.setToastr(400, 'Please select the date range!');
          return;
        }
        break;
      default:
        return;
    }

    element?.close(); // Close dropdown

    // Assign formatted dates
    this.fromDate = this.formatDate(fromDate) as any;
    this.toDate = this.formatDate(toDate) as any;

    // Fetch milestones based on project and date range
    this.getMilestones();
  }
  private formatDate(date: any): number {
    if (!date) return null;
    return new Date(date).getTime();
  }
  public getPriorityBadgeClass(priority: string): string {
    switch (priority) {
      case 'HIGH':
        return 'badge bg-danger px-3 py-1 text-white rounded-pill';
      case 'MEDIUM':
        return 'badge bg-warning px-3 py-1 text-dark rounded-pill';
      case 'LOW':
        return 'badge bg-info px-3 py-1 text-white rounded-pill';
      default:
        return 'badge bg-secondary px-3 py-1 text-white rounded-pill';
    }
  }


  public getBadgeClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'not_started':
        return ' badge bg-secondary text-white rounded-pill';
      case 'in_progress':
        return 'badge bg-info text-dark rounded-pill';
      case 'completed':
        return 'badge bg-success text-white rounded-pill';
      case 'on_hold':
        return ' badge bg-danger text-white rounded-pill';
      default:
        return 'badge bg-light text-dark rounded-pill';
    }
  }



  formatStatus(status: string): string {
    return status.replace(/_/g, ' ');
  }

}
